import type { AdminOrgRow } from "@/hooks/queries/admin-orgs-types";

// Story 15.6 — the ONE client-side derivation map for the platform organization
// surfaces (Screens 23 + 24), mirroring `admin-users-derivations.ts`.
//
// THERE IS NO SERVER-SIDE HEALTH ENUM AND NONE IS ADDED (contract B-5). Every
// band below is computed from the row payload the directory already carries:
// `contract_end_date`, `remaining`, `clinician_count`, `calibration_ready_count`.
// `member_count` is deliberately NOT among them: it answers membership, and the
// calibration band asks a clinical question (Story 15.10, 2026-09-04 ruling).
// A second surface wanting the same band IMPORTS FROM HERE — it does not
// re-derive it. Two independent spellings of "healthy" is how one screen calls an
// organization fine while the next one calls it at risk, from the same JSON.
//
// THE STATUS BAND FAILS CLOSED, for the reason `adminAccessStatePill` states in
// as many words: the row that most needs attention must not carry the most
// reassuring label. An organization whose `contract_end_date` is null — or whose
// date this file cannot parse — renders the BLOCKED "Contract dates missing"
// pill, never "Healthy".
//
// THE STATUS BAND IS TWO INDEPENDENT AXES AND BOTH RENDER (Story 15.7 AC7a).
// The contract and the seat pool are separate facts about an organization, so
// ranking them against each other can only ever hide one of the two: an
// organization whose contract ends in six weeks AND whose seats are exhausted
// needs both actions, and neither is a lesser version of the other. The PM
// ruled on 2026-08-27 that nothing wins — and rejected re-ordering as the fix,
// because a chain in the other direction just moves the masking. Ordering
// survives only WITHIN an axis, where the states are mutually exclusive points
// on one scale.
//
// The numbers are NAMED, never inline in JSX (boundary rows 16/17), following the
// shipped `SUBSCRIPTION_EXPIRY_WINDOW_DAYS` (`dashboard-derivations.ts:17`) — a
// threshold spelled into a component is a threshold nobody can find twice.

/** Every pill these two screens render. `variant` is a `Badge` variant name. */
export interface AdminOrgPill {
  variant: "ok" | "pending" | "blocked" | "info" | "warning" | "neutral";
  label: string;
}

/**
 * The em-dash placeholder for an absent value — the `admin-users-derivations.ts`
 * convention, restated here rather than imported so the org surfaces do not take
 * a dependency on the user-directory module for one character.
 *
 * A blank cell is indistinguishable from a rendering bug, and the string "null"
 * is a leaked wire value. Neither ships (boundary row 24).
 */
export const EMPTY_CELL = "—";

/**
 * How far ahead of `contract_end_date` the Status column starts saying "Renewal
 * soon". 90 days is the number the client-validated Screen 23 prints on its own
 * "Org renewals (90d)" tile (`amps-screen-23-platform-organizations.html:62`).
 *
 * Deliberately NOT the 30 of `organizations/_shared/renewal_window.py ::
 * REMINDER_WINDOW_DAYS`
 * or of `SUBSCRIPTION_EXPIRY_WINDOW_DAYS` (boundary row 17): this pill is a
 * directory SCAN AID for a platform operator, not a reminder to a tenant, and it
 * has to surface a renewal early enough to be actioned commercially.
 */
export const RENEWAL_WINDOW_DAYS = 90;

/**
 * Headroom at or below which the Status column starts saying "Near seat limit".
 *
 * No artifact names this number — the mockup names the BAND and not the
 * threshold — so it is named here, once, and changeable in one place.
 */
export const NEAR_SEAT_LIMIT_REMAINING = 5;

// THESE TWO NUMBERS ARE THE PLATFORM'S DEFINITION OF THE BANDS, not this file's
// local preference. Any surface that reports "organizations needing renewal" or
// "organizations near their seat limit" — Screen 10's operational KPIs and
// Screen 23's stat tiles, both 15.7's (see that story's IF-3) — quotes THESE, by
// import. Picking a fresh number there is not a second opinion, it is a second
// definition: the tile and the pill beneath it would then disagree about the
// same organization, from the same payload, with nothing marking which is right.
//
// The header's "import the band, do not re-derive it" does NOT cover this on its
// own. A tile renders no pill, so it can import `orgStatusPills` for the table,
// apply its own `<= 60` for the count, and look entirely consistent while being
// two products. The threshold is the shared thing, not just the derivation.
//
// The two numbers do NOT have the same standing, and treating them as one pair
// is how a settled number gets re-opened with the client. The 90 is SOURCED:
// the client-validated Screen 23 prints `Org renewals (90d)` on its own
// renewals tile, and the test beside this constant is named for that fact.
// DW-380 stays open only as a confirmation that the tile's number is also the
// band's. The 5 is genuinely unsourced — no artifact names it — and DW-381 is
// the live question. Each is pinned on BOTH sides in
// `__tests__/admin-orgs-derivations.test.ts` (+90 vs +91; 5 vs 6), so a
// confirmed number is one constant edit with the test moving alongside it.
// DW-403 asked which of these two bands outranks the other, and Story 15.7
// answered it by refusing the question: they gate DIFFERENT AXES, so neither
// ranks, and both render. The masking it recorded — `Renewal soon` hiding both
// seat bands for the last quarter of every contract — is gone with the chain.
//
// A server-side band remains refused (contract B-5): inventing one re-opens B-4,
// whose predicate the client has not answered. Two integers are facts; the pill
// is a rendering.

// The shape a bare calendar date arrives in. Anything else is unparseable as far
// as this file is concerned, and unparseable fails closed.
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const MS_PER_DAY = 86_400_000;

/**
 * Whole days from local midnight TODAY to the local midnight of `iso`, or `null`
 * when `iso` is not a bare calendar date this file can place on a calendar.
 *
 * The `T00:00:00` suffix is the `lib/format-date.ts` lesson: `new Date("2026-01-15")`
 * parses as UTC midnight, so every viewer west of UTC would see a contract expire
 * a day early. Both ends are normalised to LOCAL midnight, so the answer is a
 * count of calendar days rather than of elapsed hours.
 */
function daysUntil(iso: string, now: Date): number | null {
  if (!ISO_DATE.test(iso)) return null;
  const target = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target.getTime() - today.getTime()) / MS_PER_DAY);
}

/**
 * The payload keys the status band reads — and nothing else.
 *
 * `seat_capacity` and `seat_consumed` joined `remaining` when the seat bands
 * learned to tell "nothing sold yet" from "nearly full" (PM ruling 2026-08-30).
 * `remaining` alone cannot: it is `seat_capacity - seat_consumed`, so a brand-new
 * 5-seat organization with nobody in it and a 5-seat organization with nobody
 * LEFT in it are the same number.
 */
type OrgStatusInput = Pick<
  AdminOrgRow,
  "contract_end_date" | "remaining" | "seat_capacity" | "seat_consumed"
>;

/**
 * The CONTRACT axis, or `null` when this organization's contract says nothing
 * worth a band. Ordered, first match wins — legitimately, because one contract
 * sits at exactly one point on its own timeline: an expired contract is not
 * also "renewal soon".
 *
 *   1. no / unparseable `contract_end_date` → blocked "Contract dates missing"
 *   2. date in the past                     → blocked "Contract expired"
 *   3. within {@link RENEWAL_WINDOW_DAYS}   → pending "Renewal soon"
 *   4. otherwise                            → no band on this axis
 *
 * The mockup carries only row 3; it has no vocabulary for an expired or a
 * dateless contract, both of which the product can produce (Story 15.1 shipped
 * `contract_start_date` as the one field whose absence is unrecoverable, and
 * nothing stops an end date passing).
 *
 * Row 1 FAILS CLOSED on this axis and — since 15.7 — only on this axis. A date
 * we cannot read is no claim about the contract; it was never a reason to stop
 * reporting the seat pool, which is a separate fact we CAN read.
 *
 * Deliberately not exported. A caller that could take one axis on its own could
 * render one band and re-create the masking the split removed; the composer is
 * the only way to a rendered status.
 */
function orgContractBand(org: OrgStatusInput, now: Date): AdminOrgPill | null {
  const days = org.contract_end_date == null ? null : daysUntil(org.contract_end_date, now);

  if (days == null) return { variant: "blocked", label: "Contract dates missing" };
  if (days < 0) return { variant: "blocked", label: "Contract expired" };
  if (days <= RENEWAL_WINDOW_DAYS) return { variant: "pending", label: "Renewal soon" };
  return null;
}

/**
 * The SEAT axis, or `null` when the pool has room. Ordered for the same reason
 * the contract axis is: one pool sits at one point on one scale.
 *
 *   1. no headroom left                         → pending "At seat limit"
 *   2. `remaining <= NEAR_SEAT_LIMIT_REMAINING` → pending "Near seat limit"
 *   3. otherwise                                → no band on this axis
 *
 * Row 1 tests `remaining <= 0`, not `=== 0`. A NEGATIVE headroom means the
 * organization is over its own capacity, which is worse than sitting exactly on
 * it — and an equality test would drop that row through to the reassuring "Near
 * seat limit" instead. Same fail-closed reasoning as the missing-date branch.
 *
 * Not exported, for the reason given on {@link orgContractBand}.
 */
function orgSeatBand(org: OrgStatusInput): AdminOrgPill | null {
  // A pool that was never sold is not a pool under pressure, on either band
  // (PM ruling 2026-08-30). Without this, `remaining === 0` reports a
  // zero-capacity organization as blocking, which is the one thing "At seat
  // limit" is supposed to mean. The server's `orgs_at_seat_limit` carries the
  // matching `seat_capacity >= 1` leg so the tile and the row agree.

  if (org.seat_capacity < 1) return null;

  if (org.remaining <= 0) return { variant: "pending", label: "At seat limit" };

  // `seat_consumed >= 1` is the false-alarm fix. `NEAR_SEAT_LIMIT_REMAINING` is
  // ABSOLUTE, and `seat_capacity` is bounded `ge=1`, so a brand-new five-seat
  // organization with nobody in it rendered "Near seat limit" at 100 percent
  // headroom — and `Healthy` was unreachable for it. Not hypothetical: two
  // fixtures in this story's own seeder land exactly there.
  //
  // Gating on `consumed` rather than making the threshold proportional is the
  // cheaper half of the same fix and leaves the constant — which Screen 23's
  // client-validated copy quotes — untouched. An organization that has issued
  // even one seat is back on the absolute scale, which is what an operator
  // watching a pool fill up actually wants.
  if (org.seat_consumed >= 1 && org.remaining <= NEAR_SEAT_LIMIT_REMAINING) {
    return { variant: "pending", label: "Near seat limit" };
  }
  return null;
}

/**
 * The Status column (AC19, AC20; Story 15.7 AC7a) — EVERY band that applies, in
 * a fixed order, never a winner.
 *
 * Contract band first, seat band second. That is a reading order, not a ranking:
 * severity is not comparable across the two axes (an expired contract and an
 * exhausted pool are different kinds of broken), so a computed order would smuggle
 * back the precedence the PM ruling of 2026-08-27 rejected. A fixed order at
 * least means the column reads the same way on every row.
 *
 * Returns a single `Healthy` only when BOTH axes are clear — `Healthy` never
 * appears beside a real band, because it is a claim about the whole organization
 * rather than about one axis.
 *
 * `now` is injectable so the bands are testable without freezing the clock; it is
 * never passed in production.
 */
export function orgStatusPills(org: OrgStatusInput, now: Date = new Date()): AdminOrgPill[] {
  const bands = [orgContractBand(org, now), orgSeatBand(org)].filter(
    (band): band is AdminOrgPill => band !== null,
  );
  return bands.length > 0 ? bands : [{ variant: "ok", label: "Healthy" }];
}

/**
 * The two payload keys the calibration band reads — and nothing else.
 *
 * `clinician_count`, NOT `member_count` (Story 15.10 AC7). The two parted
 * company on 2026-09-04: `member_count` answers membership and counts the
 * owner and every org_admin, none of whom hold a clinical seat or ever
 * calibrate. Comparing readiness against it put "Ready" permanently out of
 * reach of any organization that has an administrator. Narrowing the TYPE
 * rather than only the comparison is deliberate — the band must not be able to
 * reach `member_count` at all, which is what makes the rule checkable instead
 * of a reading of the diff.
 */
type OrgCalibrationInput = Pick<
  AdminOrgRow,
  "clinician_count" | "calibration_ready_count" | "unreachable_seat_count"
>;

/**
 * The Calibration column (AC19). A PLAIN STATUS, not the mockup's `92 / 118`
 * fraction: the client answered 2026-08-25 "calibration readiness as a plain
 * status column; 'Ready' at row level" (boundary row 14).
 *
 *   • `clinician_count === 0`, none unreachable → "No clinicians"
 *   • every clinician ready, none unreachable   → "Ready"
 *   • some clinician ready                      → "In progress"
 *   • none                                      → "Not started"
 *
 * The two AFFIRMATIVE bands — "No clinicians" and "Ready" — both carry the
 * unreachable-seat clause, because both make a claim about the whole
 * organization from counters that drop the seats the projection cannot
 * describe. The two middle bands claim less and need no such guard.
 *
 * Every branch reads the CLINICAL counter (Story 15.10 AC7). An organization with
 * an owner, two administrators and no seated clinicians renders "No clinicians" —
 * it has three members and nobody who can score.
 *
 * An org with no clinicians is its OWN state rather than a vacuously "Ready" one —
 * an unstaffed organization is not a calibrated organization, and rendering it
 * green would put the freshly provisioned tenant at the top of the healthy pile.
 *
 * "Ready" tests EQUALITY, not `>=`. A ready count above the clinician count is a
 * server-side inconsistency, and the conservative answer to one is the label that
 * claims less: such a row falls through to "In progress".
 *
 * The counter behind this is resolved server-side through the calibration facade
 * alone (AC6). Do not "improve" the band by also reading `GET /calibration/status`
 * to catch the `already_certified` cohort — that is the DW-69 collapse the first
 * both-reading surface owns, and this packet is not taking it on.
 */
export function orgCalibrationPill(org: OrgCalibrationInput): AdminOrgPill {
  // BOTH affirmative bands carry the suppression, not just the green one (PM
  // ruling 2026-09-05, extended 2026-09-07). "No clinicians" is the same class
  // of claim as "Ready" — it reports the whole organization from a counter that
  // silently drops the seat rows the projection cannot describe — and it is the
  // one an operator reads as "this tenant has not staffed up yet". An
  // organization ALL of whose seat holders are unprojected folds to
  // `clinician_count: 0`, so without this clause the console denies the
  // existence of people it is merely unable to name.
  //
  // It falls through to "Not started", the label that CLAIMS LESS: it says
  // calibration has not begun, which is true of everyone we can see, and it does
  // not deny that anyone is there. A fifth band would be a mockup change on a
  // client-validated screen; withholding one is not.
  if (org.clinician_count === 0 && org.unreachable_seat_count === 0) {
    return { variant: "info", label: "No clinicians" };
  }
  // "Ready" is withheld while the organization seats people the projection
  // cannot describe (PM ruling, 2026-09-05). Both clinical counters silently
  // drop those seat rows, so an organization whose REMAINING reachable
  // clinicians are all complete satisfies the equality below and would render
  // green — for an estate that, on E14, held EIGHT such people when the staging
  // database was measured on 2026-09-07 (eleven across three organizations;
  // every one of them a real account present in both `auth` and `authz`, so the
  // seat points at somebody the projection simply never received). The count is
  // a snapshot and will move; the band must not depend on its value.
  // "Everyone we can see is ready" is a different claim from "everyone is
  // ready", and only the second one is safe in green.
  //
  // It falls through to "In progress", not to a fifth state: the equality
  // implies `calibration_ready_count > 0` whenever `clinician_count > 0`, so
  // the next branch always catches it. A new label would be a mockup change on
  // a client-validated screen; withholding one is not.
  const everyReachableClinicianIsReady = org.calibration_ready_count === org.clinician_count;
  if (everyReachableClinicianIsReady && org.unreachable_seat_count === 0) {
    return { variant: "ok", label: "Ready" };
  }
  if (org.calibration_ready_count > 0) return { variant: "pending", label: "In progress" };
  return { variant: "neutral", label: "Not started" };
}
